import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Play, RotateCcw, Shield, TrendingDown, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { API_URL, authHeaders, formatTs, BOT_LABELS } from './ops/ops_helpers';

const LEVEL_COLORS = {
  normal: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  caution: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  reduced: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  minimal: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  paused: 'bg-rose-700/30 text-rose-300 border-rose-600/40',
  disabled: 'bg-zinc-700/30 text-zinc-400 border-zinc-600/40',
};

// Regimen de mercado (GAP 2, per-simbolo en Hyperliquid).
const REGIME_COLORS = {
  trending: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  chop: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  neutral: 'bg-zinc-600/20 text-zinc-300 border-zinc-600/30',
  unknown: 'bg-zinc-700/20 text-zinc-500 border-zinc-700/30',
};
const REGIME_LABELS = {
  trending: 'tendencia', chop: 'lateral', neutral: 'neutral', unknown: '—',
};

const BOT_IDS = ['ftmo', 'hyperliquid'];

// Color del DD segun cercania al limite (FTMO, GAP 3). kind: 'daily' | 'total'.
const ddTone = (v, kind) => {
  const x = Number(v) || 0;
  const warn = kind === 'daily' ? 1.0 : 3.0;
  const bad = kind === 'daily' ? 1.5 : 4.0;
  if (x >= bad) return 'text-rose-400';
  if (x >= warn) return 'text-amber-400';
  return 'text-emerald-400';
};

const fmtMult = (v) => `${(Number(v ?? 1)).toFixed(2)}x`;

export function AdminOpsGovernor() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [history, setHistory] = useState({});
  const [selectedBot, setSelectedBot] = useState('ftmo');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/ops/governor`, { headers: authHeaders() });
      const data = r.ok ? await r.json() : { bots: [] };
      setStates(data.bots || []);

      const entries = await Promise.all(
        BOT_IDS.map(async (id) => {
          const hr = await fetch(`${API_URL}/api/ops/governor/${id}/history?limit=15`, {
            headers: authHeaders(),
          });
          const hData = hr.ok ? await hr.json() : { events: [] };
          return [id, hData.events || []];
        }),
      );
      setHistory(Object.fromEntries(entries));
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await loadAll();
    } finally {
      setLoading(false);
    }
  }, [loadAll]);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch(`${API_URL}/api/ops/governor`, { headers: authHeaders() });
        const data = r.ok ? await r.json() : { bots: [] };
        setStates(data.bots || []);
        const entries = await Promise.all(
          BOT_IDS.map(async (id) => {
            const hr = await fetch(`${API_URL}/api/ops/governor/${id}/history?limit=15`, {
              headers: authHeaders(),
            });
            const hData = hr.ok ? await hr.json() : { events: [] };
            return [id, hData.events || []];
          }),
        );
        setHistory(Object.fromEntries(entries));
      } catch (e) {
        toast.error(`Error: ${e.message}`);
      }
    })();
  }, []);

  const stateFor = (id) => states.find((s) => s.bot_id === id) || {};
  const current = stateFor(selectedBot);
  const metrics = current.metrics || {};
  const symbols = current.symbols || {};
  const symbolEntries = Object.entries(symbols).sort((a, b) => a[0].localeCompare(b[0]));

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/ops/governor/run`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Governor evaluado');
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  const resetBot = async (botId) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/governor/${botId}/reset`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`${BOT_LABELS[botId] || botId}: reset`);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleEnabled = async (botId, enabled) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/governor/${botId}/enabled`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(enabled ? 'Governor activado' : 'Governor desactivado');
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 p-4 md:p-6" data-testid="admin-ops-governor-page">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/ops')}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="governor-back-ops-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Ops Center
            </Button>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-400" />
              Capital Governor
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              data-testid="governor-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              size="sm"
              onClick={runAll}
              disabled={running}
              data-testid="governor-run-all-btn"
            >
              <Play className="w-4 h-4 mr-1" />
              Evaluar ahora
            </Button>
          </div>
        </div>

        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Gestion de riesgo por regimen</CardTitle>
            <CardDescription className="text-xs">
              Niveles 0–4: 1.0x → 0.5x → 0.25x → 0.1x → pausa. <strong className="text-amber-300">FTMO</strong> tambien
              reduce por <strong className="text-amber-300">drawdown</strong> (DD-first). <strong className="text-violet-300">Hyperliquid</strong> gobierna
              cada simbolo por separado (BTC/ETH/SOL) y detecta <strong className="text-violet-300">tendencia/lateral</strong>.
              Los bots leen <code className="text-emerald-300">risk_multiplier</code> en config remota.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {BOT_IDS.map((id) => {
                const s = stateFor(id);
                const label = s.label || 'normal';
                const tone = LEVEL_COLORS[label] || LEVEL_COLORS.normal;
                const sm = s.metrics || {};
                const ssyms = s.symbols || {};
                const ssymEntries = Object.entries(ssyms);
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`governor-bot-card-${id}`}
                    onClick={() => setSelectedBot(id)}
                    className={`text-left rounded-lg border p-4 transition-colors ${
                      selectedBot === id
                        ? 'border-emerald-500/60 bg-emerald-500/5'
                        : 'border-zinc-800 bg-zinc-950/40 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{BOT_LABELS[id] || id}</span>
                      <Badge variant="outline" className={`text-xs ${tone}`}>{label}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
                      <span>Mult: <strong className="text-zinc-200">{fmtMult(s.risk_multiplier)}</strong></span>
                      <span>Nivel: <strong className="text-zinc-200">{s.level ?? 0}</strong></span>
                      <span>PF: <strong className="text-zinc-200">{sm.profit_factor ?? '—'}</strong></span>
                      <span>Racha L: <strong className="text-rose-300">{sm.loss_streak ?? 0}</strong></span>
                    </div>

                    {id === 'ftmo' && sm.daily_dd_pct != null && (
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-zinc-400" data-testid="governor-card-dd-ftmo">
                        <span>DD dia: <strong className={ddTone(sm.daily_dd_pct, 'daily')}>{sm.daily_dd_pct}%</strong></span>
                        <span>DD total: <strong className={ddTone(sm.total_dd_pct, 'total')}>{sm.total_dd_pct}%</strong></span>
                      </div>
                    )}

                    {id === 'hyperliquid' && ssymEntries.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid="governor-card-symbols-hl">
                        {ssymEntries.map(([sym, sv]) => (
                          <Badge
                            key={sym}
                            variant="outline"
                            className={`text-[10px] ${REGIME_COLORS[sv.regime] || REGIME_COLORS.unknown}`}
                          >
                            {sym} {fmtMult(sv.risk_multiplier)} · {REGIME_LABELS[sv.regime] || sv.regime}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {s.paused && (
                      <p className="mt-2 text-xs text-rose-400 flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" /> Sin nuevas entradas
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-950 border-zinc-800" data-testid="governor-detail-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Detalle — {BOT_LABELS[selectedBot] || selectedBot}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetBot(selectedBot)}
                data-testid="governor-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Reset a normal
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleEnabled(selectedBot, current.enabled === false)}
                data-testid="governor-toggle-enabled-btn"
              >
                {current.enabled === false ? 'Activar governor' : 'Desactivar governor'}
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded border border-zinc-800 p-3">
                <p className="text-zinc-500 text-xs">Expectancy</p>
                <p className="font-mono">${metrics.expectancy ?? 0}</p>
              </div>
              <div className="rounded border border-zinc-800 p-3">
                <p className="text-zinc-500 text-xs">Win Rate</p>
                <p className="font-mono">{metrics.win_rate ?? 0}%</p>
              </div>
              <div className="rounded border border-zinc-800 p-3">
                <p className="text-zinc-500 text-xs">Muestra</p>
                <p className="font-mono">{metrics.count ?? 0} trades</p>
              </div>
              <div className="rounded border border-zinc-800 p-3">
                <p className="text-zinc-500 text-xs">Ultima eval</p>
                <p className="text-xs">{formatTs(current.last_evaluated_at)}</p>
              </div>
            </div>

            {selectedBot === 'ftmo' && metrics.daily_dd_pct != null && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="governor-ftmo-dd">
                <div className="rounded border border-zinc-800 p-3">
                  <p className="text-zinc-500 text-xs">DD diario</p>
                  <p className={`font-mono ${ddTone(metrics.daily_dd_pct, 'daily')}`}>{metrics.daily_dd_pct}%</p>
                </div>
                <div className="rounded border border-zinc-800 p-3">
                  <p className="text-zinc-500 text-xs">DD total</p>
                  <p className={`font-mono ${ddTone(metrics.total_dd_pct, 'total')}`}>{metrics.total_dd_pct}%</p>
                </div>
                <div className="rounded border border-zinc-800 p-3 col-span-2">
                  <p className="text-zinc-500 text-xs">Umbrales DD (dia / total)</p>
                  <p className="text-xs text-zinc-400">L1 0.7/2 · L2 1/3 · L3 1.5/4 · pausa 2/5 (%)</p>
                </div>
              </div>
            )}

            {current.reason && (
              <p className="text-sm text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded px-3 py-2">
                {current.reason}
              </p>
            )}

            {selectedBot === 'hyperliquid' && symbolEntries.length > 0 && (
              <div data-testid="governor-symbols-table">
                <h3 className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-violet-400" />
                  Riesgo por simbolo · peor nivel L{current.worst_level ?? 0}
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Simbolo</TableHead>
                      <TableHead className="text-zinc-400">Regimen</TableHead>
                      <TableHead className="text-zinc-400">Nivel</TableHead>
                      <TableHead className="text-zinc-400">Mult</TableHead>
                      <TableHead className="text-zinc-400">PF</TableHead>
                      <TableHead className="text-zinc-400">Trades</TableHead>
                      <TableHead className="text-zinc-400">Racha</TableHead>
                      <TableHead className="text-zinc-400">Chop</TableHead>
                      <TableHead className="text-zinc-400">Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {symbolEntries.map(([sym, sv]) => {
                      const m = sv.metrics || {};
                      return (
                        <TableRow key={sym} className="border-zinc-800" data-testid={`governor-symbol-row-${sym}`}>
                          <TableCell className="font-medium text-zinc-200">{sym}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${REGIME_COLORS[sv.regime] || REGIME_COLORS.unknown}`}>
                              {REGIME_LABELS[sv.regime] || sv.regime}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">L{sv.level}</TableCell>
                          <TableCell className="font-mono text-xs">{fmtMult(sv.risk_multiplier)}</TableCell>
                          <TableCell className="font-mono text-xs">{m.profit_factor ?? '—'}</TableCell>
                          <TableCell className="text-xs">{m.count ?? 0}</TableCell>
                          <TableCell className="text-xs text-rose-300">{m.loss_streak ?? 0}</TableCell>
                          <TableCell className="font-mono text-xs">{m.chop_score ?? '—'}</TableCell>
                          <TableCell className="text-zinc-400 text-[11px]">{sv.reason}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            <Table data-testid="governor-history-table">
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-400">Fecha</TableHead>
                  <TableHead className="text-zinc-400">Cambio</TableHead>
                  <TableHead className="text-zinc-400">Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(history[selectedBot] || []).map((ev, i) => (
                  <TableRow key={i} className="border-zinc-800">
                    <TableCell className="text-zinc-400 text-xs">{formatTs(ev.ts)}</TableCell>
                    <TableCell className="text-xs">
                      {ev.symbol ? `${ev.symbol}: ` : ''}L{ev.from_level} → L{ev.to_level}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-xs">{ev.reason}</TableCell>
                  </TableRow>
                ))}
                {!(history[selectedBot] || []).length && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-zinc-500 py-6">
                      Sin cambios registrados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminOpsGovernor;
